import Peer, { DataConnection } from 'peerjs'
import React from 'react'

const RETRY_DELAY_MS = 2000
const WAKE_DRIFT_THRESHOLD_MS = 5000
const WAKE_POLL_INTERVAL_MS = 2000

const PEER_CONFIG = {
  config: {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
    ],
  },
}

const PeerSender = ({
  peerId,
  recieverId,
}: {
  peerId: string
  recieverId: string
}) => {
  const [peerConnect, setPeerConnect] = React.useState<DataConnection>()
  const activeRef = React.useRef(true)
  const peerRef = React.useRef<Peer>()

  React.useEffect(() => {
    const handlePageHide = () => {
      peerRef.current?.destroy()
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  const attemptConnect = React.useCallback(
    (peer: Peer) => {
      if (peer.destroyed || !peer.open) {
        console.info('Connection attempt has been cancelled', {
          destroyed: peer.destroyed,
          peerOpen: peer.open,
        })
        return
      }

      if (!activeRef.current) {
        return
      }

      const conn = peer.connect(recieverId)

      if (!conn) {
        console.log('We were not able to create a connection instace')
        return
      }
      console.log('A new Connection has been instantiated...')

      setPeerConnect(conn)
      conn.on('open', () => {
        console.log('Connection established to:', recieverId)
      })

      conn.on('close', () => {
        console.warn('Connection closed, retrying in', RETRY_DELAY_MS, 'ms...')
        setTimeout(() => attemptConnect(peer), RETRY_DELAY_MS)
      })

      conn.on('error', (err) => {
        console.error('Connection error:', err)
        if (!peer.destroyed) {
          setTimeout(() => attemptConnect(peer), RETRY_DELAY_MS)
        }
      })
    },
    [activeRef]
  )

  const attemptReconnect = React.useCallback(
    (reason: string) => {
      const peer = peerRef.current
      if (!peer || peer.destroyed) return

      console.info(`Re-attempting connection after ${reason}`)

      if (peer.disconnected) {
        peer.reconnect()
        peer.once('open', () => attemptConnect(peer))
      } else if (!peerConnect?.open) {
        attemptConnect(peer)
      }
    },
    [peerConnect, attemptConnect]
  )

  const handleVisibilityChange = React.useCallback(() => {
    const visible = document.visibilityState === 'visible'
    activeRef.current = visible
    if (visible) {
      attemptReconnect('visibility restore')
    }
  }, [attemptReconnect])

  React.useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [handleVisibilityChange])

  React.useEffect(() => {
    let lastTick = performance.now()
    const driftId = setInterval(() => {
      const now = performance.now()
      const drift = now - lastTick - WAKE_POLL_INTERVAL_MS
      if (drift > WAKE_DRIFT_THRESHOLD_MS) {
        console.warn(`CPU freeze detected, drift: ${drift}ms — reconnecting`)
        attemptReconnect('wake from idle')
      }
      lastTick = now
    }, WAKE_POLL_INTERVAL_MS)
    return () => clearInterval(driftId)
  }, [attemptReconnect])

  React.useEffect(() => {
    const peer = new Peer(peerId, PEER_CONFIG)
    peerRef.current = peer
    console.log('A new Peer has been instantiated...')

    peer.on('open', (id) => {
      console.log('Sender Peer is ready with ID:', id)
      attemptConnect(peer)
    })

    peer.on('error', (err) => {
      console.error('Peer error:', err.type, err.message)
      if (peer.destroyed) {
        return
      }
      if (err.type === 'peer-unavailable') {
        setTimeout(() => attemptConnect(peer), RETRY_DELAY_MS)
        return
      }
      if (peer.disconnected) {
        setTimeout(() => {
          if (activeRef.current && peer.disconnected && !peer.destroyed) {
            peer.reconnect()
            peer.once('open', () => attemptConnect(peer))
          }
        }, RETRY_DELAY_MS)
      }
    })
    return () => {
      console.log('Peer has been destroyed.')
      peer.destroy()
    }
  }, [peerId, recieverId])

  return peerConnect
}

export default PeerSender
